import React from 'react'
import { Statistic, Card, Row, Col, Spin, Typography, Alert, Table, Avatar, Select, Button, Space, message, Modal, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { UserOutlined, DownloadOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useDrupiApi } from '../../hooks/use-drupi-api'
import { BrandDashboard, RankingEntry, MonthlyReportResponse, MonthlyReportRow, PendingPrizesResponse, PendingPrizeRow } from './types'

const { Title, Text } = Typography

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

// Escapa un valor para una celda CSV (comillas/comas/saltos de línea).
const csvCell = (v: unknown): string => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export const SocialAdminDashboardPage: React.FC = () => {
  const { drupiGet, drupiPost } = useDrupiApi()
  const navigate = useNavigate()

  const [data, setData] = React.useState<BrandDashboard | null>(null)
  const [ranking, setRanking] = React.useState<RankingEntry[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // ── Reporte mensual de puntos (para premios) ──────────────────────────────
  // Default: mes ANTERIOR (el último cerrado). Reconstruido desde snapshots → un mes
  // pasado siempre da el mismo número.
  const [repYear, setRepYear]   = React.useState<number>(() => { const n = new Date(); return n.getMonth() === 0 ? n.getFullYear() - 1 : n.getFullYear() })
  const [repMonth, setRepMonth] = React.useState<number>(() => { const n = new Date(); return n.getMonth() === 0 ? 12 : n.getMonth() })
  const [report, setReport]         = React.useState<MonthlyReportResponse | null>(null)
  const [repLoading, setRepLoading] = React.useState(false)
  const [repError, setRepError]     = React.useState<string | null>(null)

  React.useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([
      drupiGet<BrandDashboard>('/brand-admin/dashboard'),
      drupiGet<RankingEntry[]>('/brand-admin/ranking'),
    ])
      .then(([d, r]) => { if (alive) { setData(d); setRanking(r) } })
      .catch(e => { if (alive) setError(e.message) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [drupiGet])

  const fmt = (n: number) => n.toLocaleString('es-AR')

  const columns: ColumnsType<RankingEntry> = [
    { title: '#', dataIndex: 'rank', width: 56 },
    {
      title: 'Amplificador',
      key: 'user',
      render: (_, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Avatar size='small' src={r.avatar_url || undefined} icon={<UserOutlined />} style={{ marginRight: 8 }} />
          {r.username}
        </span>
      ),
    },
    {
      title: 'Puntos del mes',
      dataIndex: 'points',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.points - b.points,
      render: (v: number) => v.toLocaleString('es-AR'),
    },
    {
      title: 'Vistas del mes',
      dataIndex: 'monthly_views',
      sorter: (a, b) => (a.monthly_views ?? 0) - (b.monthly_views ?? 0),
      render: (v: number) => (v ?? 0).toLocaleString('es-AR'),
    },
    {
      title: 'Vistas totales',
      dataIndex: 'views',
      sorter: (a, b) => a.views - b.views,
      render: (v: number) => v.toLocaleString('es-AR'),
    },
    { title: 'Posts', dataIndex: 'posts', width: 90 },
  ]

  const generateReport = React.useCallback(async () => {
    setRepLoading(true); setRepError(null)
    try {
      const r = await drupiGet<MonthlyReportResponse>(`/brand-admin/report?year=${repYear}&month=${repMonth}`)
      setReport(r)
    } catch (e) {
      setRepError((e as Error).message); setReport(null)
    } finally {
      setRepLoading(false)
    }
  }, [drupiGet, repYear, repMonth])

  // ── Generar premios (movido del /admin de Social) ─────────────────────────
  // Genera (idempotente, TODAS las marcas) los premios del mes elegido en el
  // selector del reporte — que defaultea al mes ANTERIOR (el último cerrado).
  // Confirmación explícita antes de disparar (pedido de Agustín 2026-07-07).
  const [genLoading, setGenLoading] = React.useState(false)
  const generatePrizes = () => {
    Modal.confirm({
      title: '¿Estás seguro?',
      content: `Se van a generar los premios de ${MESES[repMonth - 1]} ${repYear} para TODAS las marcas (100k pts = US$100, tope US$500). Es idempotente: re-correr pisa, no duplica.`,
      okText: 'Sí, generar premios',
      cancelText: 'Cancelar',
      onOk: async () => {
        setGenLoading(true)
        try {
          const r = await drupiPost<Record<string, unknown>>(`/brand-admin/generate-prizes?year=${repYear}&month=${repMonth}`)
          message.success(`Premios de ${MESES[repMonth - 1]} ${repYear} generados. ${JSON.stringify(r)}`)
        } catch (e) {
          message.error((e as Error).message)
        } finally {
          setGenLoading(false)
        }
      },
    })
  }

  // ── Pago de premios: reporte de impagos + marcar todo como pagado ─────────
  const [pending, setPending]         = React.useState<PendingPrizesResponse | null>(null)
  const [pendLoading, setPendLoading] = React.useState(false)
  const [pendError, setPendError]     = React.useState<string | null>(null)
  const [payLoading, setPayLoading]   = React.useState(false)

  const loadPending = React.useCallback(async () => {
    setPendLoading(true); setPendError(null)
    try {
      setPending(await drupiGet<PendingPrizesResponse>('/brand-admin/prizes/pending'))
    } catch (e) {
      setPendError((e as Error).message); setPending(null)
    } finally {
      setPendLoading(false)
    }
  }, [drupiGet])

  const METODO: Record<string, string> = { mercadopago: 'Mercado Pago', bank_transfer: 'Transferencia bancaria' }

  const downloadPendingCsv = () => {
    if (!pending) return
    // Columnas pensadas para la empresa que ejecuta los pagos: quién cobra, cuánto,
    // por qué mes, y a dónde depositarle (MP o banco). `datos_completos = NO` marca
    // las filas que todavía no se pueden pagar (el usuario no cargó sus datos).
    const header = ['usuario_tiktok', 'nombre', 'email', 'pais', 'mes', 'premio_usd',
                    'metodo_de_cobro', 'alias_mp', 'banco', 'numero_de_cuenta',
                    'documento_titular', 'datos_completos', 'puntos', 'estado',
                    'generado_el', 'open_id']
    const lines = pending.rows.map(r => [
      r.username, r.full_name, r.email, r.country,
      `${r.year}-${String(r.month).padStart(2, '0')}`, r.prize_usd,
      METODO[r.payment_method] ?? '', r.mercadopago_alias, r.bank_name,
      r.bank_account_number, r.bank_holder_document,
      r.payment_data_complete ? 'SI' : 'NO', r.points, r.status,
      r.created_at, r.open_id,
    ])
    const csv = [header, ...lines].map(row => row.map(csvCell).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })   // BOM → Excel respeta acentos
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pagos-premios-pendientes-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
    message.success('CSV descargado')
  }

  const markAllPaid = () => {
    Modal.confirm({
      title: '¿Marcar TODOS los premios pendientes como pagados?',
      content: pending
        ? `Se van a marcar como pagados ${pending.count} premio(s) por un total de US$${pending.total_usd}. El usuario los va a ver como "Pagado" en Mis Premios (con el aviso de que los tiempos de acreditación varían según el método de cobro). No hay deshacer.`
        : 'Se van a marcar como pagados TODOS los premios pendientes de la marca. El usuario los va a ver como "Pagado" en Mis Premios. No hay deshacer.',
      okText: 'Sí, marcar como pagados',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        setPayLoading(true)
        try {
          const r = await drupiPost<{ marked_paid: number }>('/brand-admin/prizes/mark-all-paid')
          message.success(`${r.marked_paid} premio(s) marcados como pagados`)
          if (pending) await loadPending()   // refresca la tabla (queda vacía = confirmación visual)
        } catch (e) {
          message.error((e as Error).message)
        } finally {
          setPayLoading(false)
        }
      },
    })
  }

  const downloadCsv = () => {
    if (!report) return
    // Sin user_id (2026-08-06): era el open_id de TikTok bajo nombre legacy y no le
    // sirve a quien reparte premios — la identidad de cara afuera es el email.
    const header = ['rank', 'username', 'puntos', 'posts']
    const lines = report.rows.map(r => [r.rank, r.username, r.points, r.posts])
    const csv = [header, ...lines].map(row => row.map(csvCell).join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })   // BOM → Excel respeta acentos
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `puntos-${report.year}-${String(report.month).padStart(2, '0')}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
    message.success('CSV descargado')
  }

  const reportColumns: ColumnsType<MonthlyReportRow> = [
    { title: '#', dataIndex: 'rank', width: 56 },
    {
      title: 'Amplificador', key: 'user',
      render: (_, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Avatar size='small' src={r.avatar_url || undefined} icon={<UserOutlined />} style={{ marginRight: 8 }} />
          {r.username}
        </span>
      ),
    },
    { title: 'Puntos', dataIndex: 'points', defaultSortOrder: 'descend', sorter: (a, b) => a.points - b.points, render: (v: number) => v.toLocaleString('es-AR') },
    { title: 'Posts', dataIndex: 'posts', width: 90 },
  ]

  // Columnas de la tabla de premios impagos (el CSV lleva las mismas, más open_id).
  const pendingColumns: ColumnsType<PendingPrizeRow> = [
    { title: 'Mes', key: 'mes', width: 120, render: (_, r) => `${MESES[r.month - 1]} ${r.year}` },
    { title: 'Amplificador', dataIndex: 'username' },
    { title: 'Nombre', dataIndex: 'full_name' },
    { title: 'US$', dataIndex: 'prize_usd', width: 70, defaultSortOrder: 'descend', sorter: (a, b) => a.prize_usd - b.prize_usd },
    { title: 'País', dataIndex: 'country', width: 110 },
    { title: 'Método', key: 'metodo', render: (_, r) => METODO[r.payment_method] ?? <Text type='secondary'>—</Text> },
    {
      title: 'Datos de cobro', key: 'cobro',
      render: (_, r) => r.payment_method === 'mercadopago'
        ? (r.mercadopago_alias || <Text type='secondary'>—</Text>)
        : r.payment_method === 'bank_transfer'
          ? [r.bank_name, r.bank_account_number, r.bank_holder_document].filter(Boolean).join(' · ') || <Text type='secondary'>—</Text>
          : <Text type='secondary'>—</Text>,
    },
    {
      title: 'Datos', key: 'completos', width: 110,
      render: (_, r) => r.payment_data_complete
        ? <Tag color='green' style={{ marginRight: 0 }}>Completos</Tag>
        : <Tag color='red' style={{ marginRight: 0 }}>Faltan datos</Tag>,
    },
  ]

  const yearOptions = [0, 1, 2].map(d => { const v = new Date().getFullYear() - d; return { value: v, label: String(v) } })

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 4 }}>Amplificación social</Title>
      <Text type='secondary'>
        Cuánto amplificaron orgánicamente tus reels los usuarios amplificadores de Social que participan por puntos.
      </Text>

      {error && (
        <Alert style={{ marginTop: 16 }} type='error' showIcon message='No se pudo cargar' description={error} />
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
      ) : data ? (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {/* Comprometido = Σ por usuario de min(⌊pts/100k⌋,5)×100 — GLOBAL (todas
                las marcas), lo que se pagaría si el mes cerrara ahora. */}
            <Col xs={24} sm={12} md={8}><Card><Statistic title='Comprometido este mes' prefix='US$' value={fmt(data.committed_usd ?? 0)} /></Card></Col>
            {/* Dos números distintos: "del mes" = delta ganado desde el 1° (el que suma
                puntos); "totales" = todas las vistas acumuladas del video (absoluto). */}
            <Col xs={24} sm={12} md={8}><Card><Statistic title='Vistas del mes' value={fmt(data.monthly_views ?? 0)} /></Card></Col>
            <Col xs={24} sm={12} md={8}><Card><Statistic title='Vistas totales' value={fmt(data.total_views)} /></Card></Col>
            <Col xs={24} sm={12} md={8}><Card><Statistic title='Usuarios participando' value={fmt(data.total_amplifiers)} /></Card></Col>
            <Col xs={24} sm={12} md={8}><Card><Statistic title='Posts' value={fmt(data.total_amplifications)} /></Card></Col>
          </Row>

          <Title level={4} style={{ marginTop: 32, marginBottom: 2 }}>Ranking de amplificadores</Title>
          <Text type='secondary'>Ordenados por puntos del mes (mismo criterio que el ranking de Social). Hacé clic en uno para ver sus videos.</Text>
          <Table
            style={{ marginTop: 16 }}
            rowKey='open_id'
            columns={columns}
            dataSource={ranking}
            pagination={false}
            onRow={r => ({
              onClick: () => navigate(`/social-admin/user/${encodeURIComponent(r.open_id)}`),
              style: { cursor: 'pointer' },
            })}
          />
        </>
      ) : null}

      {/* ── Reporte mensual de puntos (para premios) ───────────────────────────
          Independiente del dashboard de arriba: se puede generar aunque el
          dashboard falle. Reconstruido desde snapshots → mes cerrado = fijo. */}
      <Card style={{ marginTop: 32 }}>
        <Title level={4} style={{ marginTop: 0, marginBottom: 2 }}>Reporte mensual de puntos</Title>
        <Text type='secondary'>
          Puntos por amplificador en el mes elegido, para repartir premios. Se reconstruye desde las
          métricas históricas, así que un mes ya cerrado siempre da el mismo número.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Space wrap>
            <Select value={repMonth} style={{ width: 150 }} onChange={setRepMonth}
              options={MESES.map((m, i) => ({ value: i + 1, label: m }))} />
            <Select value={repYear} style={{ width: 110 }} onChange={setRepYear} options={yearOptions} />
            <Button type='primary' loading={repLoading} onClick={generateReport}>Generar reporte</Button>
            <Button danger loading={genLoading} onClick={generatePrizes}>Generar premios</Button>
            {report && (
              <Button icon={<DownloadOutlined />} onClick={downloadCsv}>Descargar CSV</Button>
            )}
          </Space>
        </div>

        {repError && (
          <Alert style={{ marginTop: 16 }} type='error' showIcon message='No se pudo generar' description={repError} />
        )}

        {report && !repError && (
          <Table
            style={{ marginTop: 16 }}
            rowKey='open_id'
            size='small'
            columns={reportColumns}
            dataSource={report.rows}
            pagination={false}
            locale={{ emptyText: `Sin puntos en ${MESES[report.month - 1]} ${report.year}` }}
          />
        )}
      </Card>

      {/* ── Pago de premios ────────────────────────────────────────────────────
          Reporte de TODOS los premios aún no pagados (todas las personas y meses),
          con los datos de cobro que el usuario cargó en Settings de Social (país +
          Mercado Pago o transferencia bancaria) — pensado para entregarle el CSV a
          la empresa que ejecuta los pagos. El botón "Marcar todo como pagado" cierra
          el ciclo: el usuario pasa a ver "Pagado" en Mis Premios (con el aviso de
          que la acreditación demora según el método de cobro). */}
      <Card style={{ marginTop: 32 }}>
        <Title level={4} style={{ marginTop: 0, marginBottom: 2 }}>Pago de premios</Title>
        <Text type='secondary'>
          Premios pendientes de pago de todos los meses, con los datos de cobro de cada
          amplificador (país, Mercado Pago o banco). Descargá el CSV para la empresa que hace
          los pagos y, una vez ejecutados, marcá todo como pagado.
        </Text>
        <div style={{ marginTop: 16 }}>
          <Space wrap>
            <Button type='primary' loading={pendLoading} onClick={loadPending}>Generar reporte de pagos</Button>
            {pending && (
              <Button icon={<DownloadOutlined />} onClick={downloadPendingCsv} disabled={!pending.rows.length}>
                Descargar CSV
              </Button>
            )}
            <Button danger loading={payLoading} onClick={markAllPaid}>Marcar todo como pagado</Button>
          </Space>
        </div>

        {pendError && (
          <Alert style={{ marginTop: 16 }} type='error' showIcon message='No se pudo generar' description={pendError} />
        )}

        {pending && !pendError && (
          <>
            <Text style={{ display: 'block', marginTop: 16 }}>
              {pending.count} premio(s) pendiente(s) · total <strong>US${fmt(pending.total_usd)}</strong>
              {pending.rows.some(r => !r.payment_data_complete) && (
                <Text type='danger'> · hay filas sin datos de cobro (no se pueden pagar todavía)</Text>
              )}
            </Text>
            <Table
              style={{ marginTop: 12 }}
              rowKey={r => `${r.open_id}-${r.year}-${r.month}`}
              size='small'
              columns={pendingColumns}
              dataSource={pending.rows}
              pagination={false}
              scroll={{ x: true }}
              locale={{ emptyText: 'No hay premios pendientes de pago 🎉' }}
            />
          </>
        )}
      </Card>
    </div>
  )
}
